/**
 * batch-69 samenstellen, fase C (FO-516) - de inname-afvinklijst in de portal.
 *
 * Een innamelijst (`InnameSessie`) is de lijst van alles wat een medewerker in
 * bezit heeft, on demand opgebouwd uit dezelfde bron als de inleverlijst van de
 * offboarding (`bouwInnameRegels`, sleutel "INNAME:<soort>:<id>"). Afvinken is
 * direct inboeken; wat bij het afsluiten niet is afgevinkt gaat per item naar HR.
 * Een later gestarte offboarding neemt de lijst over via die sleutel (verwijzing,
 * geen kopie).
 *
 * Gedeelde request-schema's (API en portal valideren ermee) en de DTO's. Bedragen
 * alleen in de HR-DTO's; de medewerker ziet de toelichting zonder inkoopprijs.
 */
import { z } from 'zod'
import { idParam } from './id-validators'
import { queryBoolean } from './zod-query'
import { MiddelFotoIdSchema, type MiddelType } from './middelen'
import { OFFBOARDING_INLEVER_STATEN, type OffboardingInleverStaat } from './offboarding'
import { KOFFER_INNAME_STATEN, type KofferInnameStaat } from './samenstelling'

// ─── Enums (spiegel van Prisma) ──────────────────────────────────────────────

export const INNAME_SESSIE_STATUSSEN = ['OPEN', 'AFGESLOTEN'] as const
export type InnameSessieStatus = (typeof INNAME_SESSIE_STATUSSEN)[number]
export const INNAME_SESSIE_STATUS_LABELS: Record<InnameSessieStatus, string> = {
  OPEN: 'Bezig',
  AFGESLOTEN: 'Afgesloten',
}

export const INNAME_ITEM_STATUSSEN = ['OPEN', 'INGEBOEKT', 'NIET_INGELEVERD', 'ELDERS_AFGEHANDELD'] as const
export type InnameItemStatus = (typeof INNAME_ITEM_STATUSSEN)[number]
export const INNAME_ITEM_STATUS_LABELS: Record<InnameItemStatus, string> = {
  OPEN: 'Nog in bezit',
  INGEBOEKT: 'Ingeleverd en ingeboekt',
  NIET_INGELEVERD: 'Niet ingeleverd',
  ELDERS_AFGEHANDELD: 'Elders afgehandeld',
}

export const INNAME_VOORSTEL_STATUSSEN = [
  'TER_GOEDKEURING_HR',
  'WACHT_OP_TWEEDE_AKKOORD',
  'VERREKENEN',
  'NASTUREN',
  'AFGEBOEKT',
  'VERVALLEN',
] as const
export type InnameVoorstelStatus = (typeof INNAME_VOORSTEL_STATUSSEN)[number]
export const INNAME_VOORSTEL_STATUS_LABELS: Record<InnameVoorstelStatus, string> = {
  TER_GOEDKEURING_HR: 'Ter goedkeuring bij HR',
  WACHT_OP_TWEEDE_AKKOORD: 'Wacht op een tweede akkoord',
  VERREKENEN: 'Wordt verrekend',
  NASTUREN: 'Nasturen',
  AFGEBOEKT: 'Afgeboekt, niet verhaald',
  VERVALLEN: 'Vervallen (alsnog ingeleverd)',
}

/** Het besluit van HR per niet-afgevinkt item (FO-516 §C6). */
export const INNAME_BESLUITEN = ['VERREKENEN', 'NASTUREN', 'AFBOEKEN', 'ALSNOG_INGELEVERD'] as const
export type InnameBesluit = (typeof INNAME_BESLUITEN)[number]
export const INNAME_BESLUIT_LABELS: Record<InnameBesluit, string> = {
  VERREKENEN: 'Verrekenen',
  NASTUREN: 'Nasturen',
  AFBOEKEN: 'Afboeken (niet verhalen)',
  ALSNOG_INGELEVERD: 'Alsnog ingeleverd',
}

/** Waar een goedgekeurde verrekening landt. */
export const INNAME_VERREKEN_ROUTES = ['EINDAFREKENING', 'LOON'] as const
export type InnameVerrekenRoute = (typeof INNAME_VERREKEN_ROUTES)[number]
export const INNAME_VERREKEN_ROUTE_LABELS: Record<InnameVerrekenRoute, string> = {
  EINDAFREKENING: 'Via de eindafrekening',
  LOON: 'Via het loon (medewerker in dienst)',
}

// ─── Request-schema's ────────────────────────────────────────────────────────

const ymd = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Gebruik een datum als jjjj-mm-dd')
const reden = (min = 3) => z.string().trim().min(min, `Geef een reden (minimaal ${min} tekens)`).max(1000)
const handtekening = z
  .string()
  .regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/, 'Handtekening ontbreekt of is ongeldig')
  .max(2_000_000)
/** Bedrag als decimale string ("12,50" of "12.50"); nooit een float in de API. */
const bedrag = z
  .string()
  .trim()
  .transform((v) => v.replace(',', '.'))
  .refine((v) => /^\d{1,7}(\.\d{1,2})?$/.test(v), 'Geef een bedrag als 12,50')

/** Innamelijst openen voor een medewerker (bestaat er al een open lijst, dan die). */
export const InnameSessieOpenenSchema = z.object({
  employeeId: idParam('employeeId'),
})
export type InnameSessieOpenenInput = z.infer<typeof InnameSessieOpenenSchema>

/** Een QR-label scannen in de lijst: het item dat erbij hoort. */
export const InnameScanSchema = z.object({
  token: z.string().trim().min(1, 'Geen code gescand').max(500),
})
export type InnameScanInput = z.infer<typeof InnameScanSchema>

/**
 * Eén item afvinken = direct inboeken. Gescand of handmatig (dan met reden),
 * met de staat; bij een andere staat dan goed is minstens één foto verplicht.
 * `clientMutatieId` maakt een (offline gebufferde) herhaling idempotent.
 */
export const InnameAfvinkenSchema = z
  .object({
    conditie: z.enum(OFFBOARDING_INLEVER_STATEN),
    fotoIds: z.array(MiddelFotoIdSchema).max(5).optional(),
    opmerking: z.string().trim().max(1000).nullable().optional(),
    gescand: z.boolean(),
    handmatigReden: z.string().trim().max(1000).nullable().optional(),
    /** Waar het terugkomt (uniek exemplaar en bulk). */
    locatieId: idParam('locatieId').nullable().optional(),
    /** Bulk: het aantal stuks dat nu terugkomt (leeg = alles wat open staat). */
    aantal: z.number().int().min(1).max(9999).optional(),
    clientMutatieId: z.string().uuid().nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (!v.gescand && (v.handmatigReden?.trim().length ?? 0) < 3) {
      ctx.addIssue({ code: 'custom', path: ['handmatigReden'], message: 'Geef een reden waarom je niet scant (minimaal 3 tekens)' })
    }
    if (v.conditie !== 'GOED' && (v.fotoIds?.length ?? 0) === 0) {
      ctx.addIssue({ code: 'custom', path: ['fotoIds'], message: 'Maak minimaal een foto bij een staat anders dan goed' })
    }
  })
export type InnameAfvinkenInput = z.infer<typeof InnameAfvinkenSchema>

/**
 * Een koffer of set innemen (FO-516 §B5): per onderdeel de staat. Een
 * onvolledige koffer wordt direct ingeboekt als incomplete set; wat ontbreekt
 * staat daarna als los item op de lijst en gaat bij het afsluiten naar HR.
 */
export const InnameKofferSchema = z.object({
  locatieId: idParam('locatieId'),
  gescand: z.boolean(),
  handmatigReden: z.string().trim().max(1000).nullable().optional(),
  regels: z
    .array(
      z.object({
        inhoudRegelId: idParam('inhoudRegelId'),
        staat: z.enum(KOFFER_INNAME_STATEN),
        opmerking: z.string().trim().max(500).nullable().optional(),
        fotoIds: z.array(MiddelFotoIdSchema).max(5).optional(),
      }),
    )
    .max(500),
  notitie: z.string().trim().max(2000).nullable().optional(),
  clientMutatieId: z.string().uuid().nullable().optional(),
})
export type InnameKofferInput = z.infer<typeof InnameKofferSchema>

/** De lijst afsluiten: wat niet is afgevinkt gaat per item naar HR. */
export const InnameAfsluitenSchema = z.object({
  /** Handtekening van de medewerker (als die aanwezig is). */
  handtekeningMedewerker: handtekening.nullable().optional(),
  notitie: z.string().trim().max(2000).nullable().optional(),
})
export type InnameAfsluitenInput = z.infer<typeof InnameAfsluitenSchema>

/** Het besluit van HR over één niet-afgevinkt item. */
export const InnameBeoordelenSchema = z
  .object({
    besluit: z.enum(INNAME_BESLUITEN),
    /** Verrekenen: het bedrag (aanpasbaar t.o.v. het voorstel). */
    bedrag: bedrag.nullable().optional(),
    /** Nasturen: uiterlijk op deze dag. */
    nasturenVoor: ymd.nullable().optional(),
    reden: reden(3),
  })
  .superRefine((v, ctx) => {
    if (v.besluit === 'VERREKENEN' && !v.bedrag) {
      ctx.addIssue({ code: 'custom', path: ['bedrag'], message: 'Geef het bedrag dat wordt verrekend' })
    }
    if (v.besluit === 'NASTUREN' && !v.nasturenVoor) {
      ctx.addIssue({ code: 'custom', path: ['nasturenVoor'], message: 'Geef de termijn waarbinnen het wordt nagestuurd' })
    }
  })
export type InnameBeoordelenInput = z.infer<typeof InnameBeoordelenSchema>

/**
 * Het tweede akkoord (4-ogen, review I2): een verrekening boven de grens van de
 * middelen-instellingen (`verhaalVierOgenBoven`) wordt pas definitief als een
 * andere gebruiker akkoord geeft. Niet akkoord = terug naar HR, met de reden.
 */
export const InnameTweedeAkkoordSchema = z
  .object({
    akkoord: z.boolean(),
    reden: z.string().trim().max(1000).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (!v.akkoord && (v.reden?.trim().length ?? 0) < 3) {
      ctx.addIssue({ code: 'custom', path: ['reden'], message: 'Geef een reden (minimaal 3 tekens)' })
    }
  })
export type InnameTweedeAkkoordInput = z.infer<typeof InnameTweedeAkkoordSchema>

/** De medewerker maakt bezwaar tegen de inname-bevestiging (taak voor HR). */
export const InnameBezwaarSchema = z.object({
  toelichting: z.string().trim().min(10, 'Licht je bezwaar toe (minimaal 10 tekens)').max(2000),
})
export type InnameBezwaarInput = z.infer<typeof InnameBezwaarSchema>

/** Verrekening via het loon is uitgevoerd (salarisadministratie). */
export const InnameVerrekendSchema = z.object({
  notitie: z.string().trim().max(1000).nullable().optional(),
})
export type InnameVerrekendInput = z.infer<typeof InnameVerrekendSchema>

export const InnameMedewerkerZoekSchema = z.object({
  search: z.string().trim().max(100).optional(),
})

export const InnameSessieLijstQuerySchema = z.object({
  employeeId: idParam('employeeId').optional(),
  /** Alleen lijsten die nog open staan. */
  alleenOpen: queryBoolean().default(false),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})
export type InnameSessieLijstQuery = z.infer<typeof InnameSessieLijstQuerySchema>

/** Polling voor gezamenlijk scannen: alleen een nieuwe versie terugsturen. */
export const InnameDetailQuerySchema = z.object({
  versie: z.string().trim().max(64).optional(),
})

export const InnameTerBeoordelingQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
})

// ─── DTO's ───────────────────────────────────────────────────────────────────

export interface InnameVoorstelDTO {
  status: InnameVoorstelStatus
  /** Het berekende voorstel (FO-505 §A3); null = geen prijs bekend. */
  voorstelBedrag: string | null
  voorstelToelichting: string | null
  besluitBedrag: string | null
  besluitReden: string | null
  nasturenVoor: string | null
  verrekenRoute: InnameVerrekenRoute | null
  verrekendOp: string | null
  beoordeeldDoorNaam: string | null
  beoordeeldOp: string | null
  /** Het tweede akkoord (4-ogen) bij een verrekening boven de grens. */
  tweedeAkkoordDoorNaam: string | null
  tweedeAkkoordOp: string | null
  /** Mag de ingelogde gebruiker het tweede akkoord geven (HR, niet de eerste beoordelaar)? */
  magTweedeAkkoord: boolean
  /**
   * Overgenomen door de offboarding (regel op de inleverlijst): vanaf dan beslist
   * HR in de offboarding-cockpit en volgt deze lijst die regel.
   */
  inOffboarding: boolean
}

export interface InnameKofferOnderdeelDTO {
  inhoudRegelId: string
  omschrijving: string
  aantal: number
  code: string | null
  /** Status van de inhoudsregel vóór de inname (AANWEZIG, ONTBREEKT, ...). */
  status: string
}

export interface InnameItemDTO {
  id: string
  bronSubKey: string
  soort: string
  omschrijving: string
  kenmerk: string | null
  detail: string | null
  middelType: MiddelType | null
  aantal: number
  uitgegevenOp: string | null
  uitgifteFotoAantal: number
  status: InnameItemStatus
  /** Koffer of set: afvinken = per onderdeel de staat. */
  koffer: { dragerId: string; onderdelen: InnameKofferOnderdeelDTO[] } | null
  /** Onderdeel van een koffer dat bij de inname ontbrak of defect was. */
  ouderItemId: string | null
  /** Uniek exemplaar en bulk: een terugleglocatie is nodig. */
  vereistLocatie: boolean
  conditie: OffboardingInleverStaat | null
  opmerking: string | null
  gescand: boolean
  handmatigReden: string | null
  aantalIngeleverd: number
  ingeboektOp: string | null
  ingeboektDoorNaam: string | null
  /** Bij NIET_INGELEVERD: het voorstel en het besluit van HR. */
  voorstel: InnameVoorstelDTO | null
}

export interface InnameSessieDetailDTO {
  id: string
  nummer: string
  status: InnameSessieStatus
  employeeId: string
  medewerkerNaam: string
  /** De offboarding die de lijst heeft overgenomen. */
  offboardingWorkflowId: string | null
  geopendDoorNaam: string | null
  geopendOp: string
  gepauzeerd: boolean
  gepauzeerdDoorNaam: string | null
  afgeslotenOp: string | null
  afgeslotenDoorNaam: string | null
  notitie: string | null
  voortgang: { afgevinkt: number; totaal: number; label: string }
  items: InnameItemDTO[]
  /** De inname-bevestiging (PDF) in het dossier. */
  bevestigingDocumentId: string | null
  bezwaar: { op: string; toelichting: string; afgehandeld: boolean } | null
  /** Mag de ingelogde gebruiker afvinken en afsluiten (doelgroep inname-bevoegd)? */
  magInnemen: boolean
  /** Mag de ingelogde gebruiker de niet-afgevinkte items beoordelen (HR)? */
  magBeoordelen: boolean
  /** Mag de ingelogde gebruiker een verrekening via het loon afmelden (salarisadministratie of HR)? */
  magVerrekenen: boolean
  /** Polling: verandert bij elke wijziging. */
  versie: string
}

export interface InnameSessieRijDTO {
  id: string
  nummer: string
  status: InnameSessieStatus
  employeeId: string
  medewerkerNaam: string
  geopendOp: string
  geopendDoorNaam: string | null
  afgeslotenOp: string | null
  afgevinkt: number
  totaal: number
  terGoedkeuring: number
  inOffboarding: boolean
}

export interface InnameScanResultaatDTO {
  ok: boolean
  melding: string
  /** Het item dat bij de scan hoort (open het afvinkscherm). */
  itemId: string | null
}

export interface InnameActieResultaatDTO {
  detail: InnameSessieDetailDTO
  melding: string
  waarschuwingen: string[]
}

/** Eén niet-afgevinkt item ter beoordeling bij HR (lijst en goedkeuringen-inbox). */
export interface InnameTerBeoordelingDTO {
  itemId: string
  sessieId: string
  nummer: string
  employeeId: string
  medewerkerNaam: string
  omschrijving: string
  kenmerk: string | null
  aantal: number
  afgeslotenOp: string | null
  voorstelBedrag: string | null
  voorstelToelichting: string | null
  /** De route die een verrekening krijgt (eindafrekening of loon). */
  route: InnameVerrekenRoute
  /** TER_GOEDKEURING_HR of WACHT_OP_TWEEDE_AKKOORD (dan met het eerste besluit). */
  status: InnameVoorstelStatus
  besluitBedrag: string | null
  beoordeeldDoorNaam: string | null
}

/**
 * Wat HR besloot maar nog niet is afgerond (FO-516 §C6 "openstaande
 * verrekeningen", review K4): een verrekening die op de loonstrook of op de
 * offboarding wacht, en wat nagestuurd wordt (met de termijn).
 */
export interface InnameOpenstaandDTO {
  itemId: string
  sessieId: string
  nummer: string
  employeeId: string
  medewerkerNaam: string
  omschrijving: string
  kenmerk: string | null
  aantal: number
  status: InnameVoorstelStatus
  verrekenRoute: InnameVerrekenRoute | null
  bedrag: string | null
  nasturenVoor: string | null
  /** De nasturen-termijn is voorbij (de dagelijkse controle zet het dan terug naar HR). */
  termijnVerlopen: boolean
  beoordeeldDoorNaam: string | null
  beoordeeldOp: string | null
}

/** Portal: de eigen innames van de medewerker (zonder inkoopprijs). */
export interface MijnInnameDTO {
  id: string
  nummer: string
  afgeslotenOp: string
  bevestigingDocumentId: string | null
  ingeleverd: Array<{ omschrijving: string; kenmerk: string | null; conditie: OffboardingInleverStaat | null }>
  open: Array<{
    omschrijving: string
    kenmerk: string | null
    status: InnameVoorstelStatus
    bedrag: string | null
    toelichting: string | null
    nasturenVoor: string | null
  }>
  bezwaar: { op: string; afgehandeld: boolean } | null
  /** Bezwaar maken kan zolang er nog geen bezwaar loopt. */
  magBezwaarMaken: boolean
}

export interface InnameMedewerkerOptieDTO {
  id: string
  naam: string
  employeeNumber: string | null
  /** Er loopt al een open innamelijst voor deze medewerker. */
  openSessieId: string | null
  uitDienst: string | null
}

/** De staat van een koffer-onderdeel (zelfde vocabulaire als de set-inname). */
export type InnameKofferStaat = KofferInnameStaat
